import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FaCalendarAlt, FaMapMarkerAlt, FaArrowLeft } from 'react-icons/fa';
import { PiShoppingCartDuotone, PiNewspaperDuotone } from 'react-icons/pi';

import api from '../../helpers/Api';
import MessageError from '../../components/MessageError';
import { SkBox, SkLine } from '../../components/Skeleton';

function SkeletonDetail() {
    return (
        <div className="bg-[#0f0e17] min-h-screen py-10 px-4">
            <div className="max-w-5xl mx-auto flex flex-col gap-8">
                <div className="flex flex-col items-center gap-4">
                    <SkBox className="h-10 w-2/3 rounded-xl" />
                </div>
                <SkBox className="w-full rounded-2xl" style={{ aspectRatio: '16/9' }} />
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <div className="lg:col-span-2 flex flex-col gap-3">
                        <SkLine h="h-4" />
                        <SkLine h="h-4" w="w-5/6" />
                        <SkLine h="h-4" />
                        <SkLine h="h-4" w="4/5" />
                        <SkLine h="h-4" w="w-5/6" />
                    </div>
                    <SkBox className="h-48 rounded-2xl" />
                </div>
            </div>
        </div>
    );
}

export function Render({ newsDetail }) {
    const [selectedImage, setSelectedImage] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        if (newsDetail?.images?.length > 0) {
            setSelectedImage(newsDetail.images[0]);
        }
    }, [newsDetail]);

    const getImageUrl = (image) => {
        if (image?.id) return `${api.defaults.baseURL}/news-image-proxy?id=${image.id}`;
        return null;
    };

    const mainImg = getImageUrl(selectedImage);

    return (
        <div className="bg-[#0f0e17] min-h-screen text-white">
            <div className="max-w-5xl mx-auto px-4 py-10 flex flex-col gap-8">

                <button
                    onClick={() => navigate('/news')}
                    className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors text-sm w-fit"
                >
                    <FaArrowLeft size={13} />
                    Volver a noticias
                </button>

                <div className="text-center">
                    <span className="text-brand font-semibold text-xs uppercase tracking-widest font-mono">Noticia</span>
                    <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mt-2 leading-tight font-display">
                        {newsDetail.title}
                    </h1>
                </div>

                <div className="w-full rounded-2xl overflow-hidden border border-white/10 bg-[#16151c]">
                    {mainImg ? (
                        <img
                            src={mainImg}
                            alt={newsDetail.title}
                            className="w-full max-h-[65vh] object-contain"
                        />
                    ) : (
                        <div className="w-full h-64 flex items-center justify-center">
                            <PiNewspaperDuotone className="text-gray-600 text-6xl" />
                        </div>
                    )}
                </div>

                {newsDetail.images?.length > 1 && (
                    <div className="flex gap-3 justify-center flex-wrap">
                        {newsDetail.images.map((img, i) => (
                            <button
                                key={i}
                                onClick={() => setSelectedImage(img)}
                                className={`w-20 h-14 rounded-xl overflow-hidden border-2 transition-all duration-200 ${selectedImage?.id === img.id
                                        ? 'border-brand shadow-lg shadow-brand/20'
                                        : 'border-white/10 hover:border-white/30'
                                    }`}
                            >
                                <img
                                    src={getImageUrl(img)}
                                    alt={`Imagen ${i + 1}`}
                                    className="w-full h-full object-cover"
                                />
                            </button>
                        ))}
                    </div>
                )}

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

                    <div className="lg:col-span-2 bg-[#16151c] border border-white/10 rounded-2xl p-6 sm:p-8">
                        <h2 className="text-lg font-bold text-white mb-5 flex items-center gap-2 font-display">
                            <span className="w-1 h-5 bg-brand rounded-full" />
                            Detalles de la Noticia
                        </h2>
                        <div className="text-gray-300 leading-relaxed text-sm sm:text-base whitespace-pre-wrap">
                            {newsDetail.content_new || "Contenido no disponible."}
                        </div>
                    </div>

                    <div className="bg-[#16151c] border border-white/10 rounded-2xl p-6 h-fit flex flex-col gap-5">
                        <h3 className="text-base font-bold text-white flex items-center gap-2 font-display">
                            <span className="w-1 h-5 bg-brand rounded-full" />
                            Información del Evento
                        </h3>

                        {newsDetail.name_event && (
                            <div>
                                <p className="text-xs text-brand uppercase tracking-wider font-semibold mb-1 font-mono">Evento</p>
                                <p className="text-white font-medium">{newsDetail.name_event}</p>
                            </div>
                        )}

                        {newsDetail.event_date && (
                            <div>
                                <p className="text-xs text-brand uppercase tracking-wider font-semibold mb-1 font-mono">Fecha</p>
                                <div className="flex items-center gap-2 text-gray-300 text-sm">
                                    <FaCalendarAlt className="text-brand shrink-0" />
                                    {new Date(newsDetail.event_date).toLocaleDateString('es-ES', {
                                        year: 'numeric', month: 'long', day: 'numeric'
                                    })}
                                </div>
                            </div>
                        )}

                        {newsDetail.location && (
                            <div>
                                <p className="text-xs text-brand uppercase tracking-wider font-semibold mb-1 font-mono">Ubicación</p>
                                <div className="flex items-center gap-2 text-gray-300 text-sm">
                                    <FaMapMarkerAlt className="text-brand shrink-0" />
                                    {newsDetail.location}
                                </div>
                            </div>
                        )}

                        <button
                            onClick={() => navigate('/events')}
                            className="event-one__btn thm-btn w-full mt-2"
                        >
                            <PiShoppingCartDuotone size={18} />
                            Comprar Entradas
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default function DetailNews() {
    const { id } = useParams();
    const [newsDetail, setNewsDetail] = useState(null);
    const [error, setError] = useState(false);

    useEffect(() => {
        if (!id) return;
        api.get(`/NewsEvent?id=${id}`)
            .then(r => setNewsDetail(r.data))
            .catch(() => setError(true));
    }, [id]);

    if (error) return <MessageError title="Error al cargar la noticia." description="Por favor, intenta de nuevo." />;
    if (!newsDetail) return <SkeletonDetail />;
    return <Render newsDetail={newsDetail} />;
}
